import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog } from '@/components/ui/Dialog'
import { MorphSelect, type MorphSelectOption } from '@/components/ui/MorphSelect'
import { useDiagramStore, useSelectionStore } from '@/stores'
import { useDraftStore } from '@/stores/draftStore'
import type { CardinalityKind } from '@/utils/relationships'
import { getRelationshipCardinalities, relationTypeFromCardinalities } from '@/utils/relationships'

/** Mesma altura do trigger do MorphSelect (`size="sm"`), para a barra alinhar. */
const BAR_BUTTON = 'inline-flex h-[30px] items-center rounded-lg border px-3 text-[11px]'

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="truncate text-[10px]" style={{ color: 'var(--text-dim)' }}>
        {label}
      </span>
      <span className="flex h-[30px] min-w-0 items-center truncate text-[11px]" title={value}>
        {value}
      </span>
    </div>
  )
}

export function RelationPopup() {
  const { t } = useTranslation()
  const readOnly = useDraftStore((s) => s.mode === 'view')
  const selectedRelationshipId = useSelectionStore((state) => state.selectedRelationshipId)
  const rels = useDiagramStore((state) => state.rels)
  const tables = useDiagramStore((state) => state.tables)
  const updateRelationship = useDiagramStore((state) => state.updateRelationship)
  const deleteRelationship = useDiagramStore((state) => state.deleteRelationship)
  const invertRelationship = useDiagramStore((state) => state.invertRelationship)
  const updateTable = useDiagramStore((state) => state.updateTable)
  const selectRelationship = useSelectionStore((state) => state.selectRelationship)
  const clearSelection = useSelectionStore((state) => state.clearSelection)

  const relation = useMemo(
    () => rels.find((entry) => entry.id === selectedRelationshipId) ?? null,
    [rels, selectedRelationshipId],
  )

  const [fromCardinality, setFromCardinality] = useState<CardinalityKind>('many')
  const [toCardinality, setToCardinality] = useState<CardinalityKind>('one')
  const [errorMessage, setErrorMessage] = useState('')
  const [pendingDelete, setPendingDelete] = useState(false)

  useEffect(() => {
    if (!relation) return
    const cardinalities = getRelationshipCardinalities(relation)
    setFromCardinality(cardinalities.from)
    setToCardinality(cardinalities.to)
    setErrorMessage('')
    setPendingDelete(false)
  }, [relation])

  if (!relation) return null

  // Rotulos so ficam prontos aqui porque dependem da relacao selecionada.
  const receivingOptions: MorphSelectOption<CardinalityKind>[] = [
    { value: 'zero-many', label: t('relationBar.zeroMany') },
    { value: 'many', label: t('relationBar.oneMany') },
    { value: 'zero-one', label: t('relationBar.zeroOne') },
    { value: 'one', label: t('relationBar.exactlyOne') },
  ]
  const sourceOptions: MorphSelectOption<CardinalityKind>[] = [
    { value: 'zero-one', label: t('relationBar.optional') },
    { value: 'one', label: t('relationBar.required') },
  ]

  const destinationTable = tables.find((table) => table.id === relation.from) ?? null
  const sourceTable = tables.find((table) => table.id === relation.to) ?? null

  const save = () => {
    setErrorMessage('')

    if (relation.type === 'N-N') {
      setErrorMessage(t('relation.nnChangeBlocked'))
      return
    }

    const nextType = relationTypeFromCardinalities(fromCardinality, toCardinality)

    if (destinationTable) {
      updateTable(destinationTable.id, {
        cols: destinationTable.cols.map((column) =>
          column.name === relation.col
            ? {
                ...column,
                notNull: toCardinality === 'one',
                blank: toCardinality === 'zero-one',
              }
            : column,
        ),
      })
    }

    updateRelationship(relation.id, {
      type: nextType,
      fromCardinality,
      toCardinality,
    })
    selectRelationship(relation.id)
    setErrorMessage('')
  }

  const handleInvert = () => {
    setErrorMessage('')

    if (relation.type === 'N-N') {
      setErrorMessage(t('relation.nnChangeBlocked'))
      return
    }

    const inverted = invertRelationship(relation.id)
    if (!inverted) {
      setErrorMessage(t('relation.nnChangeBlocked'))
      return
    }

    selectRelationship(inverted.id)
  }

  return (
    <>
      {/* `items-end` alinha tudo pela linha dos controles: campos de leitura,
          selects e botoes tem rotulos de alturas diferentes, e alinhar pelo
          centro deixava os botoes flutuando acima dos inputs. */}
      <div
        className="flex w-full items-end gap-2 rounded-2xl border px-3 py-3 shadow-2xl"
        style={{
          background: 'color-mix(in srgb, var(--bg-popup) 92%, transparent)',
          borderColor: 'rgba(99,102,241,.24)',
          backdropFilter: 'blur(10px)',
          color: 'var(--text)',
        }}
      >
        <div className="grid min-w-0 flex-1 items-end gap-2 lg:grid-cols-[minmax(0,0.75fr)_minmax(0,0.75fr)_minmax(0,0.55fr)_minmax(250px,1fr)]">
          <ReadOnlyField label={t('relationBar.source')} value={sourceTable?.fullName || relation.to} />
          <ReadOnlyField label={t('relationBar.target')} value={destinationTable?.fullName || relation.from} />
          <ReadOnlyField label={t('relationBar.fk')} value={relation.col} />
          {!readOnly && (
            <div className="grid min-w-0 grid-cols-2 items-end gap-2">
              <MorphSelect<CardinalityKind>
                size="sm"
                label={t('relationBar.receivingCardinality', 'Quem recebe a FK')}
                ariaLabel={t('relationBar.receivingCardinality', 'Quem recebe a FK')}
                value={fromCardinality}
                options={receivingOptions}
                disabled={relation.type === 'N-N'}
                menuPlacement="up"
                minMenuWidth={190}
                onChange={(next) => {
                  if (next) setFromCardinality(next)
                }}
              />
              <MorphSelect<CardinalityKind>
                size="sm"
                label={t('relationBar.sourceCardinality', 'De onde vem o ID')}
                ariaLabel={t('relationBar.sourceCardinality', 'De onde vem o ID')}
                value={toCardinality}
                options={sourceOptions}
                disabled={relation.type === 'N-N'}
                menuPlacement="up"
                minMenuWidth={190}
                onChange={(next) => {
                  if (next) setToCardinality(next)
                }}
              />
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-end gap-2">
          {!readOnly && (
            <>
              <button
                type="button"
                onClick={save}
                className={BAR_BUTTON + ' font-medium'}
                style={{ borderColor: 'var(--accent)', background: 'rgba(99,102,241,.14)', color: 'var(--accent)' }}
              >
                {t('common.save')}
              </button>
              <button
                type="button"
                onClick={handleInvert}
                className={BAR_BUTTON}
                style={{ borderColor: 'var(--border)', background: 'var(--bg-card)', color: 'var(--text)' }}
                title={t('relation.invertTitle')}
              >
                {t('relationBar.invert')}
              </button>
              <button
                type="button"
                onClick={() => setPendingDelete(true)}
                className={BAR_BUTTON}
                style={{ borderColor: 'var(--color-danger-border)', color: 'var(--color-danger)', background: 'transparent' }}
              >
                {t('relationBar.delete')}
              </button>
            </>
          )}
          <button
            type="button"
            onClick={clearSelection}
            aria-label={t('common.close')}
            className="inline-flex h-[30px] w-[30px] items-center justify-center rounded-lg text-[14px]"
            style={{ color: 'var(--text-dim)' }}
          >
            ×
          </button>
        </div>
      </div>
      {errorMessage ? (
        <div className="w-[min(760px,calc(100vw-32px))] rounded-xl border px-3 py-3 text-[11px] shadow-2xl" style={{ borderColor: 'var(--color-warning-border)', background: 'var(--bg-modal)', color: 'var(--color-warning)' }}>
          {errorMessage}
        </div>
      ) : null}

      <Dialog
        open={!readOnly && pendingDelete}
        title={t('relation.delete')}
        onClose={() => setPendingDelete(false)}
        onConfirm={() => {
          deleteRelationship(relation.id)
          setPendingDelete(false)
          clearSelection()
        }}
        footer={(
          <>
            <button type="button" className="rounded-lg border px-3 py-2 text-[11px]" style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }} onClick={() => setPendingDelete(false)}>{t('common.cancel')}</button>
            <button
              type="button"
              className="rounded-lg border px-3 py-2 text-[11px]"
              style={{ borderColor: 'var(--color-danger-border)', color: 'var(--color-danger)' }}
              onClick={() => {
                deleteRelationship(relation.id)
                setPendingDelete(false)
                clearSelection()
              }}
            >
              {t('common.delete')}
            </button>
          </>
        )}
      >
        {t('relation.deleteQuestion')}
      </Dialog>
    </>
  )
}
